import { RegisterOptions, useFormContext, get } from 'react-hook-form';
import { ReactElement } from 'react';
import clsx from 'clsx';

export type CustomDropdownOption = { label: string; value: string };

type DropdownProps = {
  id: string;
  label?: string;
  validationObject?: RegisterOptions;
  options: CustomDropdownOption[];
  noMarginTop?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
};

/**
 * A dropdown react component.
 * @param props - DropdownProps.
 * @returns - React element to capture user selection.
 */
export default function CustomDropdown(props: DropdownProps): ReactElement {
  const { id, label, options, noMarginTop, validationObject, isDisabled, tooltip } = props;
  const {
    register,
    formState: { errors }
  } = useFormContext();
  return (
    <div className={`w-full ${!noMarginTop && 'mt-4'}`}>
      {label && (
        <label htmlFor={id} className="text-gray-500 dark:text-gray-500 text-sm flex items-center gap-1">
          <span>{label}</span>
          {validationObject?.required ? <span> *</span> : ''}
          {tooltip && (
            <div className="relative group/tooltip flex items-center">
              <span className="material-icons-outlined cursor-pointer text-base text-gray-400 leading-none">
                info
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-60 p-2 text-xs text-white bg-gray-900 rounded-md transition-all duration-100 scale-0 origin-top group-hover/tooltip:scale-100">
                <p className="text-start">{tooltip}</p>
              </div>
            </div>
          )}
        </label>
      )}
      <select
        disabled={isDisabled}
        id={id}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register(id, validationObject)}
        className={clsx(
          'relative w-full capitalize py-2 mt-1 text-base font-normal text-gray-300 dark:text-gray-300 rounded bg-gray-50 dark:bg-container-500 border focus:dark:border-primary-300 focus-within:dark:ring-transparent',
          { 'border-error-300': get(errors, id) },
          { 'border-gray-700': !get(errors, id) }
        )}
      >
        <option value="" aria-hidden="true" />
        <hr />
        {options.map((option: CustomDropdownOption) => (
          <option
            className="font-normal text-gray-500 dark:text-gray-200 py-4 capitalize"
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
      <p className="text-error-500 mt-1"> {get(errors, id)?.message}</p>
    </div>
  );
}
